import { getPatientData, getPractitionerForReport } from "../../api/Individual";
import { decryptData } from "../../EncryptDecrypt";

const practitionerCache = new Map();
const patientCache = new Map();

function personRecord(payload) {
  if (!payload || typeof payload !== "object") return null;
  for (const candidate of [payload.result, payload.data, payload]) {
    if (!candidate || typeof candidate !== "object") continue;
    const record = Array.isArray(candidate) ? candidate[0] : candidate;
    if (record?.name?.[0]?.text || typeof record?.name === "string") return record;
  }
  return null;
}

function readableName(payload) {
  const name = displayPersonName(personRecord(payload) || payload);
  return name && name !== "Unknown" ? name : "";
}

export function practitionerIdFromUser(record) {
  if (!record || typeof record !== "object") return "";
  // A bare Person/user id is never a scheduling identity. Overview selections
  // carry an explicitly typed Practitioner; directory rows carry FHIR links.
  if (record.resourceType === "Practitioner") return practitionerId(record.id);
  for (const item of Array.isArray(record.link) ? record.link : []) {
    const reference = item?.target?.reference;
    if (typeof reference !== "string" || !reference.startsWith("Practitioner/")) continue;
    const id = practitionerId(reference.slice("Practitioner/".length));
    if (id) return id;
  }
  return "";
}

function practitionerId(value) {
  return typeof value === "string" && /^[A-Za-z0-9.-]+$/.test(value) ? value : "";
}

export function displayPractitionerName(record) {
  const name = displayPersonName(record);
  return name === "Unknown" ? "Practitioner" : name;
}

export function displayPersonName(record) {
  const unwrapped = personRecord(record) || record;
  const raw = unwrapped?.name?.[0]?.text || (typeof unwrapped?.name === "string" ? unwrapped.name : "");
  try {
    return decryptData(raw) || raw || "Unknown";
  } catch {
    return raw || "Unknown";
  }
}

async function nameFrom(loader) {
  try {
    return readableName(await loader());
  } catch {
    return "";
  }
}

export async function loadPractitionerName(practitionerRef) {
  if (!practitionerId(practitionerRef)) return "Practitioner";
  if (practitionerCache.has(practitionerRef)) return practitionerCache.get(practitionerRef);
  const name = await nameFrom(() => getPractitionerForReport({ practitionerId: practitionerRef }));
  // Missing names must not change identity or remain cached after a transient failure.
  if (name) practitionerCache.set(practitionerRef, name);
  return name || "Practitioner";
}

function identifierRecord(payload) {
  if (!payload || typeof payload !== "object") return null;
  for (const candidate of [payload.result, payload.data, payload]) {
    if (!candidate || typeof candidate !== "object") continue;
    const record = Array.isArray(candidate) ? candidate[0] : candidate;
    if (Array.isArray(record?.identifier)) return record;
  }
  return null;
}

export function patientIdentifierFrom(payload) {
  const identifiers = identifierRecord(payload)?.identifier || [];
  const match = identifiers.find((item) => item?.type === "HCN") || identifiers.find((item) => item?.value);
  const value = match?.value;
  if (!value || typeof value !== "string") return "";
  try {
    return decryptData(value) || value;
  } catch {
    return value;
  }
}

export async function loadPatientInfo(patientRef) {
  if (!patientRef) return { name: "Unknown", identifier: "" };
  if (patientCache.has(patientRef)) return patientCache.get(patientRef);
  try {
    const payload = await getPatientData(patientRef);
    const info = { name: readableName(payload) || patientRef, identifier: patientIdentifierFrom(payload) };
    patientCache.set(patientRef, info);
    return info;
  } catch {
    const info = { name: patientRef, identifier: "" };
    patientCache.set(patientRef, info);
    return info;
  }
}

export async function loadPatientName(patientRef) {
  return (await loadPatientInfo(patientRef)).name;
}

export function seedPractitionerName(practitionerRef, name) {
  if (practitionerRef && name) practitionerCache.set(practitionerRef, name);
}
